import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Play, ArrowRight, Square, Maximize, Minimize, Users, Radio,
} from 'lucide-react';
import { Survey, SurveyQuestionDraft } from '../types';
import { fetchSurveyById, fetchSurveyQuestions } from '../services/surveyService';
import {
  LivePhase, LiveResults, setLiveState, getLiveResults, subscribeLiveState, sendLiveState, unsubscribe,
} from '../services/liveService';

const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];
const getText = (t: any): string => (t && (t.en || Object.values(t)[0])) || '';
const optLabel = (config: any, value: string): string => {
  const o = config?.options?.find((x: any) => String(x.id) === String(value));
  return o ? getText(o.label) : String(value);
};

const LivePresentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<SurveyQuestionDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<LivePhase>('LOBBY');
  const [idx, setIdx] = useState(-1);
  const [results, setResults] = useState<LiveResults | null>(null);
  const [qr, setQr] = useState<string>('');
  const [full, setFull] = useState(false);
  const channelRef = useRef<any>(null);

  const joinUrl = `${window.location.origin}/#/live/${id}`;
  const currentQ = idx >= 0 && idx < questions.length ? questions[idx] : null;

  useEffect(() => {
    (async () => {
      try {
        const [s, qs] = await Promise.all([fetchSurveyById(id!), fetchSurveyQuestions(id!)]);
        setSurvey(s); setQuestions(qs);
        const QR: any = await import('qrcode');
        setQr(await QR.toDataURL(joinUrl, { width: 420, margin: 1 }));
        channelRef.current = subscribeLiveState(id!, () => {}); // host channel (to broadcast)
        await setLiveState(id!, 'LOBBY', null);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
    return () => { unsubscribe(channelRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Presenter polls the aggregate (ONE client) — decoupled from vote volume.
  useEffect(() => {
    if (phase !== 'QUESTION' || !currentQ) return;
    let alive = true;
    const tick = async () => { try { const r = await getLiveResults(id!, currentQ.id); if (alive) setResults(r); } catch { /* ignore */ } };
    tick();
    const h = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentQ?.id]);

  const go = async (nextIdx: number, nextPhase: LivePhase) => {
    const q = nextPhase === 'QUESTION' && nextIdx >= 0 && nextIdx < questions.length ? questions[nextIdx] : null;
    setIdx(nextIdx); setPhase(nextPhase); setResults(null);
    await setLiveState(id!, nextPhase, q?.id ?? null);
    sendLiveState(channelRef.current, {
      phase: nextPhase,
      question: q ? { id: q.id, question_text: q.question_text, question_type: q.question_type, config: q.config } : null,
    });
  };

  const start = () => go(0, 'QUESTION');
  const next = () => (idx + 1 < questions.length ? go(idx + 1, 'QUESTION') : go(-1, 'ENDED'));
  const end = () => go(-1, 'ENDED');

  const toggleFull = () => {
    if (!document.fullscreenElement) { document.documentElement.requestFullscreen?.().catch(() => {}); setFull(true); }
    else { document.exitFullscreen?.().catch(() => {}); setFull(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="animate-spin text-indigo-500" size={40} /></div>;

  const total = results?.total || 0;
  const counts = results?.counts || [];
  const max = Math.max(1, ...counts.map((c) => c.count));

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      {/* top bar */}
      <div className="flex items-center justify-between px-5 py-3 bg-slate-900/70 shrink-0">
        <button onClick={() => navigate('/research/campaigns')} className="text-slate-400 hover:text-white flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Exit</button>
        <span className="flex items-center gap-2 text-indigo-300 font-semibold"><Radio size={16} className="animate-pulse" /> LIVE · {getText(survey?.title)}</span>
        <button onClick={toggleFull} className="text-slate-400 hover:text-white">{full ? <Minimize size={20} /> : <Maximize size={20} />}</button>
      </div>

      {/* stage */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-10 relative overflow-hidden">
        <div className="absolute top-[-20%] right-[-10%] w-[60%] h-[60%] bg-indigo-600/20 rounded-full blur-[160px] pointer-events-none" />

        {phase === 'LOBBY' && (
          <div className="relative z-10 text-center">
            <h1 className="text-3xl md:text-5xl font-black mb-2">{getText(survey?.title)}</h1>
            <p className="text-slate-400 text-lg md:text-2xl mb-8">Scan to join — answers appear here live</p>
            {qr && <img src={qr} alt="Join QR" className="mx-auto rounded-2xl bg-white p-4 w-64 h-64 md:w-80 md:h-80" />}
            <p className="mt-5 text-indigo-300 font-mono text-lg break-all">{joinUrl}</p>
          </div>
        )}

        {phase === 'QUESTION' && currentQ && (
          <div className="relative z-10 w-full max-w-6xl">
            <div className="flex items-start justify-between gap-6 mb-6 md:mb-10">
              <h2 className="text-3xl md:text-6xl font-black leading-tight">{getText(currentQ.question_text)}</h2>
              {qr && <img src={qr} alt="Join" className="rounded-xl bg-white p-2 w-24 h-24 md:w-32 md:h-32 shrink-0" />}
            </div>
            {['MULTIPLE_CHOICE', 'CHECKBOX', 'RANKING', 'RATING'].includes(currentQ.question_type) ? (
              <div className="space-y-3 md:space-y-5">
                {(currentQ.question_type === 'RATING'
                  ? ratingRows(currentQ, counts)
                  : (currentQ.config?.options || []).map((o: any) => ({ label: getText(o.label), count: counts.find((c) => String(c.value) === String(o.id))?.count || 0 }))
                ).map((row, i) => {
                  const pct = total ? Math.round((row.count / total) * 100) : 0;
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-xl md:text-3xl font-bold mb-1.5"><span>{row.label}</span><span className="text-slate-400">{pct}% · {row.count}</span></div>
                      <div className="h-8 md:h-12 bg-slate-800 rounded-xl overflow-hidden">
                        <div className="h-full rounded-xl transition-all duration-500" style={{ width: `${(row.count / max) * 100}%`, background: COLORS[i % COLORS.length] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-400 text-2xl">This question type isn't shown live. Use multiple-choice or rating questions.</p>
            )}
          </div>
        )}

        {phase === 'ENDED' && (
          <div className="relative z-10 text-center">
            <h1 className="text-5xl md:text-7xl font-black mb-3">Thank you!</h1>
            <p className="text-slate-400 text-xl md:text-2xl">The live session has ended.</p>
          </div>
        )}
      </div>

      {/* controls */}
      <div className="shrink-0 bg-slate-900/80 px-5 py-4 flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-slate-300 text-lg font-bold"><Users size={18} className="text-indigo-400" /> {total}</span>
        <span className="text-slate-500 text-sm">{phase === 'QUESTION' ? `Question ${idx + 1} / ${questions.length}` : phase}</span>
        <div className="flex items-center gap-2">
          {phase === 'LOBBY' && <button onClick={start} disabled={questions.length === 0} className="bg-indigo-600 hover:bg-indigo-500 px-6 py-2.5 rounded-lg font-bold flex items-center gap-2 disabled:opacity-50"><Play size={18} /> Start</button>}
          {phase === 'QUESTION' && <>
            <button onClick={end} className="bg-slate-700 hover:bg-slate-600 px-4 py-2.5 rounded-lg font-medium flex items-center gap-2"><Square size={16} /> End</button>
            <button onClick={next} className="bg-indigo-600 hover:bg-indigo-500 px-6 py-2.5 rounded-lg font-bold flex items-center gap-2">{idx + 1 < questions.length ? <>Next <ArrowRight size={18} /></> : <>Finish <Square size={16} /></>}</button>
          </>}
          {phase === 'ENDED' && <button onClick={() => go(0, 'QUESTION')} className="bg-indigo-600 hover:bg-indigo-500 px-6 py-2.5 rounded-lg font-bold flex items-center gap-2"><Play size={18} /> Restart</button>}
        </div>
      </div>
    </div>
  );
};

// Build rating rows (min..max) with live counts.
function ratingRows(q: SurveyQuestionDraft, counts: { value: string; count: number }[]) {
  const min = q.config?.min ?? 1, max = q.config?.max ?? 5;
  const rows = [];
  for (let v = min; v <= max; v++) rows.push({ label: String(v), count: counts.find((c) => String(c.value) === String(v))?.count || 0 });
  return rows;
}

export default LivePresentView;
